import React, { useState } from 'react';
import { 
  GripVertical, 
  Settings, 
  Trash2, 
  Maximize2, 
  Minimize2,
  MoreHorizontal,
  Move
} from 'lucide-react';
import type { DashboardWidget, WidgetSize } from '../../../types/dashboard-widgets';

interface WidgetCardProps {
  widget: DashboardWidget;
  children: React.ReactNode;
  isEditMode: boolean;
  onRemove: () => void;
  onConfigure?: () => void;
  onResize?: (size: WidgetSize) => void;
  isDragging?: boolean;
  dragHandleProps?: any;
}

export function WidgetCard({
  widget,
  children,
  isEditMode,
  onRemove,
  onConfigure,
  onResize,
  isDragging = false,
  dragHandleProps
}: WidgetCardProps) {
  const [showMenu, setShowMenu] = useState(false);

  // Size classes mapping — aligned with WidgetSize type ('xs'|'sm'|'md'|'lg'|'xl')
  const sizeClasses: Record<WidgetSize, string> = {
    xs: 'col-span-1 row-span-1',
    sm: 'col-span-1 row-span-1',
    md: 'col-span-1 md:col-span-2 row-span-1',
    lg: 'col-span-1 md:col-span-2 lg:col-span-3 row-span-1',
    xl: 'col-span-1 md:col-span-2 lg:col-span-4 row-span-2'
  };

  // Taille effective avec fallback sur 'md' si non définie
  const effectiveSize: WidgetSize = widget.size ?? 'md';

  const nextSize = (current: WidgetSize): WidgetSize => {
    const order: WidgetSize[] = ['xs', 'sm', 'md', 'lg', 'xl'];
    const idx = order.indexOf(current);
    return order[(idx + 1) % order.length];
  };

  return (
    <div
      className={`
        group relative
        ${sizeClasses[effectiveSize]}
        bg-card border border-border rounded-xl
        shadow-sm hover:shadow-md
        transition-all duration-200 ease-in-out
        ${isDragging ? 'opacity-50 scale-95 rotate-1 z-50' : ''}
        ${isEditMode ? 'ring-2 ring-primary/20 ring-offset-2 ring-offset-background' : ''}
      `}
    >
      {/* Drag handle - visible only in edit mode */}
      {isEditMode && (
        <div
          {...dragHandleProps}
          className="absolute -top-2 -left-2 z-10 p-1.5 bg-primary text-primary-foreground rounded-lg cursor-grab active:cursor-grabbing shadow-lg hover:bg-primary/90 transition-colors"
        >
          <Move className="h-3.5 w-3.5" />
        </div>
      )}

      {/* Widget Header - subtle, shows on hover or edit mode */}
      <div className={`
        absolute top-0 left-0 right-0 
        flex items-center justify-between 
        px-3 py-2
        ${isEditMode ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
        transition-opacity duration-200
        bg-gradient-to-b from-background/80 to-transparent
        rounded-t-xl
        pointer-events-none
      `}>
        <h3 className="text-xs font-medium text-muted-foreground truncate pr-2">
          {widget.title}
        </h3>
        
        {/* Actions */}
        <div className="flex items-center gap-1 pointer-events-auto">
          {onResize && (
            <button
              onClick={() => onResize(nextSize(effectiveSize))}
              className="p-1 rounded hover:bg-muted transition-colors"
              title="Redimensionner"
            >
              <Maximize2 className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
          
          {onConfigure && (
            <button
              onClick={onConfigure}
              className="p-1 rounded hover:bg-muted transition-colors"
              title="Configurer"
            >
              <Settings className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
          
          {isEditMode && (
            <button
              onClick={onRemove}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
              title="Supprimer"
            >
              <Trash2 className="h-3 w-3 text-red-500" />
            </button>
          )}
        </div>
      </div>

      {/* Widget Content */}
      <div className="p-4 pt-8 h-full overflow-hidden">
        {children}
      </div>

      {/* Edit mode indicator */}
      {isEditMode && (
        <div className="absolute inset-0 border-2 border-dashed border-primary/30 rounded-xl pointer-events-none" />
      )}
    </div>
  );
}
